import { Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Context as AuthContext } from '../../../../../contexts/AuthContext';
import PerformanceImprovementService from '../../../../../services/PerformanceImprovement';
import ImprovementCard from './ImprovementCard';
import SummaryModalRecordRow from '../components/SummaryModalRecordRow';

interface PerformanceImprovementItem {
  id: number;
  name: string;
  date: string;
  dateOfIncident?: string;
  position: string;
  department: string;
  supervisor: string;
  date_of_hire: string;
  dateOfHire?: string;
  policy_violation: string;
  policyViolation?: { name: string };
  reason_for_performance_improvement_plan: string;
  reasonForPerformanceImprovementPlan?: string;
  expectations_and_goals: string;
  expectationsAndGoals?: string;
  timeline_and_deadline: string;
  timelineAndDeadline?: string;
  resources_and_support: string;
  resourcesAndSupport?: string;
  private_notes?: string;
  privateNotes?: string;
  created_at: string;
  createdAt?: string;
}

interface PerformanceImprovementSummaryModalProps {
  isOpen: boolean;
  toggle: () => void;
  userId?: string | null;
  userName?: string;
  performanceImprovementData?: PerformanceImprovementItem[];
}

const PerformanceImprovementSummaryModal = ({
  isOpen,
  toggle,
  userId,
  userName,
  performanceImprovementData,
}: PerformanceImprovementSummaryModalProps) => {
  const [performanceImprovements, setPerformanceImprovements] = useState<PerformanceImprovementItem[]>([]);
  const [selected, setSelected] = useState<PerformanceImprovementItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useContext(AuthContext);

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return moment.utc(date).format('MM/DD/YYYY');
  };

  const loadPerformanceImprovements = useCallback(async () => {
    if (performanceImprovementData) {
      setPerformanceImprovements(performanceImprovementData);
      return;
    }

    if (!userId || !token) {
      return;
    }

    setIsLoading(true);
    try {
      const service = PerformanceImprovementService(token);
      const response = await service.getUserPerformanceImprovements(userId, 'performance-improvement');
      if (response?.data) {
        setPerformanceImprovements(response.data);
      }
    } catch (error: any) {
      console.error('Error loading performance improvements:', error);
      toast.error(error?.response?.data?.message || 'Error loading performance improvements');
    } finally {
      setIsLoading(false);
    }
  }, [userId, token, performanceImprovementData]);

  useEffect(() => {
    if (isOpen) {
      setSelected(null);
      loadPerformanceImprovements();
    }
  }, [isOpen, loadPerformanceImprovements]);

  const handleClose = () => {
    setSelected(null);
    setPerformanceImprovements([]);
    toggle();
  };

  const getPolicyViolationText = (item: PerformanceImprovementItem) => {
    if (item.policyViolations && Array.isArray(item.policyViolations)) {
      const joined = item.policyViolations.map((pv: any) => pv.name || pv).join(', ');
      if (joined) {
        return joined;
      }
    }
    return item.policy_violation ?? item.policyViolation?.name ?? '';
  };

  const getListDescription = (item: PerformanceImprovementItem) =>
    getPolicyViolationText(item) ||
    item.reason_for_performance_improvement_plan ||
    item.reasonForPerformanceImprovementPlan ||
    '—';

  const renderDetailCard = (performanceImprovement: PerformanceImprovementItem, index: number) => (
    <ImprovementCard
      key={performanceImprovement.id || index}
      index={index}
      name={performanceImprovement.name}
      date={formatDate(performanceImprovement.date || performanceImprovement.dateOfIncident)}
      position={performanceImprovement.position}
      department={performanceImprovement.department}
      supervisor={performanceImprovement.supervisor}
      dateOfHire={formatDate(performanceImprovement.date_of_hire ?? performanceImprovement.dateOfHire)}
      policyViolation={getPolicyViolationText(performanceImprovement) || 'N/A'}
      reasonForPerformanceImprovementPlan={
        performanceImprovement.reason_for_performance_improvement_plan ??
        performanceImprovement.reasonForPerformanceImprovementPlan ??
        ''
      }
      expectationsAndGoals={
        performanceImprovement.expectations_and_goals ?? performanceImprovement.expectationsAndGoals ?? ''
      }
      timelineAndDeadline={
        performanceImprovement.timeline_and_deadline ?? performanceImprovement.timelineAndDeadline ?? ''
      }
      resourcesAndSupport={
        performanceImprovement.resources_and_support ?? performanceImprovement.resourcesAndSupport ?? ''
      }
      privateNotes={performanceImprovement.private_notes ?? performanceImprovement.privateNotes}
      createdAt={formatDate(performanceImprovement.created_at ?? performanceImprovement.createdAt)}
    />
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      {selected ? (
        <div className="modal-header align-items-start">
          <button
            type="button"
            className="btn btn-link p-0 mr-3 text-dark mt-1"
            onClick={() => setSelected(null)}
            aria-label="Back"
          >
            <i className="fas fa-arrow-left fa-lg" />
          </button>
          <div className="flex-grow-1">
            <h5 className="mb-0 modal-title">Performance Improvement</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
          <button type="button" className="close" onClick={handleClose} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      ) : (
        <ModalHeader toggle={handleClose} tag="div">
          <div>
            <h5 className="mb-0 modal-title">Performance Improvement</h5>
            {userName ? <div className="text-muted small">{userName}</div> : null}
          </div>
        </ModalHeader>
      )}
      <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto', paddingTop: selected ? '1rem' : '0' }}>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : selected ? (
          <div className="p-1">{renderDetailCard(selected, 0)}</div>
        ) : (
          <>
            {performanceImprovements.length === 0 ? (
              <div className="text-center text-muted py-4">
                <i className="fas fa-inbox fa-3x mb-3" />
                <p>No performance improvement records found</p>
              </div>
            ) : (
              <div className="pt-2">
                {performanceImprovements.map((performanceImprovement, index) => (
                  <SummaryModalRecordRow
                    key={performanceImprovement.id || index}
                    dateText={formatDate(performanceImprovement.date || performanceImprovement.dateOfIncident)}
                    description={getListDescription(performanceImprovement)}
                    onClick={() => setSelected(performanceImprovement)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default PerformanceImprovementSummaryModal;
